import {
  AddonTriggerContext,
  Avatar,
  FlexBox,
  InputRichText,
  SelectionList,
  SelectionListSelectedItem,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { SearchUser } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useContext, useEffect } from 'react';
import { Waypoint } from 'react-waypoint';

import { useLazyReadSearchInfiniteQuery } from 'apis/kraken';
import {
  createMentionSpan,
  moveCaretAfterMention,
} from 'components/editorMentions/utils/mentions.utils';
import { getUserDisplayName } from 'utils/user';

import styles from './mentionsTrigger.module.scss';

export const MentionsTrigger = (): JSX.Element => {
  const [
    readSearchUsers,
    {
      results: users,
      isUninitialized,
      isFetching,
      isSuccess,
      hasNextPage,
      fetchNextPage,
    },
  ] = useLazyReadSearchInfiniteQuery<'users'>();

  const editorContext = useContext(InputRichText.Context);
  const { matchText, insertContent } = useContext(AddonTriggerContext);

  const searchTerm = matchText.trim().slice(1);

  const handleSelectMention = useCallback(
    (selected: SelectionListSelectedItem<SearchUser>) => {
      const mention = createMentionSpan(selected.value);
      insertContent(mention.content, { inline: true });
      // Position the caret after the inserted mention when agent is selected with click/tap.
      moveCaretAfterMention(editorContext.ref.current, mention.id);

      ampli.mentionUserSelected();
    },
    [insertContent, editorContext],
  );

  useEffect(() => {
    if (searchTerm) {
      readSearchUsers(
        {
          scope: 'users',
          query: searchTerm,
          filters: {
            strategy: 'all',
            filters: [
              {
                field: 'user_type',
                operator: 'is_one',
                values: [{ value: 'member' }],
              },
            ],
          },
        },
        { debounceTime: 300 },
      );
    }
  }, [readSearchUsers, searchTerm]);

  return (
    <FlexBox flexDirection="column" maxHeight="320px" role="menu" aria-expanded>
      <FlexBox
        alignItems="center"
        justifyContent="center"
        borderColor="neutral.200"
        borderWidth="0 0 100 0"
        padding="200"
        minWidth="280px"
      >
        <Text.Paragraph variant="4" subdued>
          {users.length > 0 && searchTerm
            ? `Matching @${searchTerm}`
            : 'Mention someone or press Esc to close'}
        </Text.Paragraph>
      </FlexBox>

      <SelectionList<SearchUser>
        className={styles.list}
        isActive
        onChange={(e) => handleSelectMention(e.selectedItem)}
      >
        {users.map((user) => {
          const name = getUserDisplayName(user);

          return (
            <SelectionList.Item
              key={user.id}
              value={user}
              contentClassName={styles.itemContent}
              noFixedSelectionStateSpace
            >
              <Avatar user={user} />

              <div>
                <Text.Heading variant="heading-4">{name}</Text.Heading>

                {user.email !== name && (
                  <Text.Paragraph variant="4" subdued>
                    {user.email}
                  </Text.Paragraph>
                )}
              </div>
            </SelectionList.Item>
          );
        })}

        {isUninitialized && (
          <FlexBox
            as="li"
            padding="200"
            justifyContent="center"
            alignItems="center"
          >
            <Text.Paragraph variant="4" subdued>
              Type agent name or email.
            </Text.Paragraph>
          </FlexBox>
        )}

        {isFetching && (
          <FlexBox
            as="li"
            padding="300"
            justifyContent="center"
            alignItems="center"
          >
            <Spinner size={20} />
          </FlexBox>
        )}

        {isSuccess && !users.length && (
          <FlexBox as="li" padding="200" justifyContent="center">
            <Text.Paragraph variant="4" subdued>
              No agents found.
            </Text.Paragraph>
          </FlexBox>
        )}

        {hasNextPage && !isFetching && <Waypoint onEnter={fetchNextPage} />}
      </SelectionList>
    </FlexBox>
  );
};
